// apps/frontend/src/components/common/GlowpCatchGame.tsx
// Mini-jeu marketplace Glowp — un panier qui attrape les items qui tombent
// (mode, déco, créations, bijoux, livres, art). Animation native via
// requestAnimationFrame, contrôle au curseur/doigt. Bonus dorés rares qui
// valent gros. Score + best score persistés.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Shirt,
  ShoppingBag,
  Gem,
  Palette,
  BookOpen,
  Home,
  Watch,
  Heart,
  Sparkles,
  Trophy,
  X,
  Brush,
} from 'lucide-react';

// Inventaire des items qui tombent — couvre tout l'univers Glowp
// (pas uniquement la mode). Chaque item a un poids relatif pour ajuster
// la fréquence d'apparition.
type ItemKind =
  | 'shirt'
  | 'bag'
  | 'gem'
  | 'palette'
  | 'book'
  | 'home'
  | 'watch'
  | 'heart'
  | 'brush'
  | 'bonus';

interface ItemDef {
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  weight: number; // pour la sélection pondérée
  points: number;
  bg: string; // gradient pour la pastille
  iconColor: string;
}

const ITEMS: Record<ItemKind, ItemDef> = {
  shirt: {
    Icon: Shirt,
    weight: 12,
    points: 1,
    bg: 'linear-gradient(135deg, #F5EFE6 0%, #E8DAC7 100%)',
    iconColor: '#B89968',
  },
  bag: {
    Icon: ShoppingBag,
    weight: 12,
    points: 1,
    bg: 'linear-gradient(135deg, #F5EFE6 0%, #E8DAC7 100%)',
    iconColor: '#B89968',
  },
  gem: {
    Icon: Gem,
    weight: 8,
    points: 2,
    bg: 'linear-gradient(135deg, #EFE3D2 0%, #D9C5A6 100%)',
    iconColor: '#8B6914',
  },
  palette: {
    Icon: Palette,
    weight: 8,
    points: 2,
    bg: 'linear-gradient(135deg, #EFE3D2 0%, #D9C5A6 100%)',
    iconColor: '#8B6914',
  },
  book: {
    Icon: BookOpen,
    weight: 10,
    points: 1,
    bg: 'linear-gradient(135deg, #F5EFE6 0%, #E8DAC7 100%)',
    iconColor: '#B89968',
  },
  home: {
    Icon: Home,
    weight: 10,
    points: 1,
    bg: 'linear-gradient(135deg, #F5EFE6 0%, #E8DAC7 100%)',
    iconColor: '#B89968',
  },
  watch: {
    Icon: Watch,
    weight: 8,
    points: 2,
    bg: 'linear-gradient(135deg, #EFE3D2 0%, #D9C5A6 100%)',
    iconColor: '#8B6914',
  },
  heart: {
    Icon: Heart,
    weight: 6,
    points: 2,
    bg: 'linear-gradient(135deg, #EFE3D2 0%, #D9C5A6 100%)',
    iconColor: '#8B6914',
  },
  brush: {
    Icon: Brush,
    weight: 8,
    points: 2,
    bg: 'linear-gradient(135deg, #EFE3D2 0%, #D9C5A6 100%)',
    iconColor: '#8B6914',
  },
  bonus: {
    Icon: Sparkles,
    weight: 2, // rare
    points: 5,
    bg: 'linear-gradient(135deg, #E8DAC7 0%, #C8A882 100%)',
    iconColor: '#FFFFFF',
  },
};

const ITEM_KINDS = Object.keys(ITEMS) as ItemKind[];
const TOTAL_WEIGHT = ITEM_KINDS.reduce((s, k) => s + ITEMS[k].weight, 0);

function pickItemKind(): ItemKind {
  let r = Math.random() * TOTAL_WEIGHT;
  for (const k of ITEM_KINDS) {
    r -= ITEMS[k].weight;
    if (r <= 0) return k;
  }
  return 'shirt';
}

interface FallingItem {
  id: number;
  kind: ItemKind;
  xPct: number; // position horizontale en % de la largeur du board (0-100)
  y: number; // position verticale en pixels (0 = top du board)
  vy: number; // vitesse en px/sec
  rotation: number;
  vRotation: number;
}

interface FloatingScore {
  id: number;
  xPct: number;
  y: number;
  text: string;
  bornAt: number;
}

interface Props {
  onClose: () => void;
}

const BOARD_HEIGHT = 280;
const BASKET_WIDTH = 64;
const BASKET_HEIGHT = 44;
const BASKET_Y = BOARD_HEIGHT - BASKET_HEIGHT - 8;
const ITEM_SIZE = 36;
const BASE_SPEED = 110; // px/sec
const BASE_SPAWN_MS = 950;
const FLOATING_SCORE_MS = 700;

const GlowpCatchGame: React.FC<Props> = ({ onClose }) => {
  const boardRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<FallingItem[]>([]);
  const floatersRef = useRef<FloatingScore[]>([]);
  const basketXPctRef = useRef(50);
  const lastFrameRef = useRef<number>(0);
  const lastSpawnRef = useRef<number>(0);
  const nextIdRef = useRef(1);
  const scoreRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const [, setTick] = useState(0); // force re-render à chaque frame
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState<number>(() => {
    try {
      return parseInt(localStorage.getItem('glowp_catch_best') || '0', 10);
    } catch {
      return 0;
    }
  });

  // Intervalle de spawn diminue avec le score (plus difficile)
  const currentSpawnMs = () =>
    Math.max(380, BASE_SPAWN_MS - Math.floor(scoreRef.current / 8) * 50);
  const currentSpeed = () =>
    BASE_SPEED + Math.floor(scoreRef.current / 10) * 12;

  const spawnItem = useCallback(() => {
    const kind = pickItemKind();
    itemsRef.current.push({
      id: nextIdRef.current++,
      kind,
      xPct: 6 + Math.random() * 88,
      y: -ITEM_SIZE,
      vy: currentSpeed() * (0.85 + Math.random() * 0.3),
      rotation: Math.random() * 30 - 15,
      vRotation: (Math.random() - 0.5) * 60,
    });
  }, []);

  const addScore = useCallback(
    (points: number, xPct: number, y: number) => {
      const newScore = scoreRef.current + points;
      scoreRef.current = newScore;
      setScore(newScore);
      if (newScore > bestScore) {
        setBestScore(newScore);
        try {
          localStorage.setItem('glowp_catch_best', String(newScore));
        } catch {
          /* localStorage indispo */
        }
      }
      floatersRef.current.push({
        id: nextIdRef.current++,
        xPct,
        y,
        text: `+${points}`,
        bornAt: performance.now(),
      });
    },
    [bestScore]
  );

  // Game loop
  useEffect(() => {
    let mounted = true;
    const tick = (now: number) => {
      if (!mounted) return;
      const dt = lastFrameRef.current ? (now - lastFrameRef.current) / 1000 : 0;
      lastFrameRef.current = now;

      // Spawn
      if (now - lastSpawnRef.current > currentSpawnMs()) {
        lastSpawnRef.current = now;
        spawnItem();
      }

      const board = boardRef.current;
      const boardW = board?.clientWidth ?? 360;
      const basketLeftPx =
        (basketXPctRef.current / 100) * boardW - BASKET_WIDTH / 2;
      const basketRightPx = basketLeftPx + BASKET_WIDTH;
      const basketTopPx = BASKET_Y;

      // Update items + collision basket
      const stillAlive: FallingItem[] = [];
      for (const item of itemsRef.current) {
        item.y += item.vy * dt;
        item.rotation += item.vRotation * dt;

        const itemCenterPx = (item.xPct / 100) * boardW;
        const itemLeftPx = itemCenterPx - ITEM_SIZE / 2;
        const itemRightPx = itemCenterPx + ITEM_SIZE / 2;
        const itemBottomY = item.y + ITEM_SIZE;

        // Collision avec le panier (item descend dans la zone)
        const inBasketYZone =
          itemBottomY >= basketTopPx && item.y <= basketTopPx + BASKET_HEIGHT;
        const xOverlap =
          itemRightPx > basketLeftPx && itemLeftPx < basketRightPx;

        if (inBasketYZone && xOverlap) {
          const def = ITEMS[item.kind];
          addScore(def.points, item.xPct, basketTopPx);
          continue; // item attrapé : on le retire
        }

        // Sorti par le bas → disparaît silencieusement (pas de pénalité)
        if (item.y > BOARD_HEIGHT) continue;

        stillAlive.push(item);
      }
      itemsRef.current = stillAlive;

      // Cleanup floaters expirés
      floatersRef.current = floatersRef.current.filter(
        f => now - f.bornAt < FLOATING_SCORE_MS
      );

      setTick(t => (t + 1) % 1_000_000);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [spawnItem, addScore]);

  // Contrôle pointer/touch
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const board = boardRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    basketXPctRef.current = pct;
  }, []);

  return (
    <div className="bg-white rounded-2xl ring-1 ring-black/5 shadow-sm overflow-hidden">
      {/* Header score */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
        <div className="flex items-center gap-3 text-sm">
          <span className="font-semibold text-brand-700">Score : {score}</span>
          {bestScore > 0 && (
            <>
              <span className="text-gray-300">·</span>
              <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-medium">
                <Trophy className="w-3 h-3" />
                {bestScore}
              </span>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
          aria-label="Quitter"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Zone de jeu */}
      <div
        ref={boardRef}
        onPointerMove={handlePointerMove}
        className="relative w-full overflow-hidden cursor-none touch-none select-none"
        style={{
          height: BOARD_HEIGHT,
          background:
            'linear-gradient(180deg, #FBF7F0 0%, #F5EFE6 60%, #E8DAC7 100%)',
        }}
      >
        {/* Hint */}
        {score === 0 && itemsRef.current.length === 0 && (
          <div className="absolute inset-x-0 top-3 flex justify-center pointer-events-none">
            <span className="text-[11px] text-gray-500 bg-white/70 backdrop-blur-sm px-2.5 py-1 rounded-full">
              Bouge le panier pour attraper les pépites
            </span>
          </div>
        )}

        {/* Items qui tombent */}
        {itemsRef.current.map(item => {
          const def = ITEMS[item.kind];
          const Icon = def.Icon;
          return (
            <div
              key={item.id}
              className="absolute pointer-events-none"
              style={{
                left: `${item.xPct}%`,
                top: item.y,
                width: ITEM_SIZE,
                height: ITEM_SIZE,
                transform: `translateX(-50%) rotate(${item.rotation}deg)`,
                willChange: 'transform, top',
              }}
            >
              <div
                className="w-full h-full rounded-full flex items-center justify-center shadow-md"
                style={{
                  background: def.bg,
                  color: def.iconColor,
                }}
              >
                <Icon className="w-4 h-4" strokeWidth={2} />
              </div>
            </div>
          );
        })}

        {/* Floating scores */}
        {floatersRef.current.map(f => {
          const age = (performance.now() - f.bornAt) / FLOATING_SCORE_MS;
          return (
            <div
              key={f.id}
              className="absolute pointer-events-none font-bold text-sm"
              style={{
                left: `${f.xPct}%`,
                top: f.y - age * 30,
                transform: 'translateX(-50%)',
                color: '#8B6914',
                opacity: 1 - age,
              }}
            >
              {f.text}
            </div>
          );
        })}

        {/* Panier */}
        <div
          className="absolute pointer-events-none transition-transform"
          style={{
            left: `${basketXPctRef.current}%`,
            top: BASKET_Y,
            width: BASKET_WIDTH,
            height: BASKET_HEIGHT,
            transform: 'translateX(-50%)',
          }}
        >
          <div
            className="w-full h-full rounded-2xl flex items-center justify-center shadow-lg"
            style={{
              background:
                'linear-gradient(135deg, #C8A882 0%, #B89968 50%, #8B6914 100%)',
            }}
          >
            <ShoppingBag className="w-5 h-5 text-white" strokeWidth={2.2} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlowpCatchGame;
